import type { ChapterApiResponse } from './articleControllerTypes';
import { wireArticleMedia } from './articleMediaWiring';
import { byId } from './domUtils';
import { escapeHtml } from './formatUtils';
import { renderMarkdownToSafeHtml, renderMermaidBlocks } from './markdownRenderer';
import type { Root } from './prototypeTypes';
import { renderChapterMeteredTail } from '../../shared/pages/chapterMeteredTail';
import { disposeKeywordCloud, renderKeywordCloud } from './keywordCloud';

const renderVersions = new WeakMap<HTMLElement, number>();

/** Paints the chapter content and its free-visible answer blocks. */
export function paintArticleBody(root: Root, response: ChapterApiResponse): void {
	const body = byId(root, 'article-body');
	const skeleton = byId(root, 'article-skeleton');
	const rendered = byId(root, 'article-body-rendered');
	const errorBox = byId(root, 'article-body-error');
	if (skeleton) skeleton.hidden = true;
	if (errorBox) errorBox.hidden = true;
	const summary = byId(root, 'article-summary');
	const summaryCopy = byId(root, 'article-summary-copy');
	const summaryPanel = byId(root, 'article-summary-panel');
	const chapterSubtitle = response.chapter?.subtitle?.trim() ?? '';
	const chapterSummary = response.chapter?.summary?.trim() ?? '';
	// Subtitle is optional: only the stored value renders. No summary-derived
	// fallback -- an empty subtitle hides the hero subtitle slot entirely.
	const chapterHeroSubtitle = chapterSubtitle;
	const title = byId(root, 'article-title');
	if (title) {
		title.classList.remove('topic-hero-loading');
		title.textContent = response.chapter?.title?.trim() || 'Chapter';
	}
	if (summary) {
		summary.classList.remove('topic-hero-loading');
		summary.textContent = chapterHeroSubtitle;
		summary.hidden = !chapterHeroSubtitle;
	}
	if (summaryCopy) {
		summaryCopy.classList.remove('article-summary-loading');
		summaryCopy.textContent = chapterSummary;
	}
	const hasSummary = Boolean(chapterSummary);
	if (summaryPanel) {
		summaryPanel.hidden = !hasSummary;
		(summaryPanel as HTMLDetailsElement).open = true;
	}
	const hasTakeaways = paintTakeaways(root, response);
	const informationBand = byId(root, 'chapter-info-band');
	if (informationBand) {
		informationBand.hidden = !hasSummary && !hasTakeaways && !(response.chapter?.keywords?.length);
		informationBand.classList.remove('chapter-info-band--single');
	}
	paintChapterKeywordCloud(root, response, chapterSummary);
	const actionLoading = byId(root, 'article-hero-actions-loading');
	actionLoading?.setAttribute('hidden', '');
	actionLoading?.parentElement?.classList.remove('chapter-actions--loading');
	paintFaqs(root, response);
	const locked = isChapterLocked(response);
	paintMarkdown(body, rendered, locked, response.chapter?.visibleBody ?? '');
	paintMeteredTail(root, locked, response.access?.text.gate ?? 'paywall', response.access?.requiredPlan, response.access?.requiredCapability);
	wireArticleMedia(root, response);
}

function paintMeteredTail(
	root: Root,
	locked: boolean,
	gate: 'login' | 'paywall' | null,
	requiredPlan?: 'lite' | 'pro' | 'max' | null,
	requiredCapability?: 'githubDeepDive' | null,
): void {
	const container = byId(root, 'article-metered-tail');
	if (!container) return;
	container.hidden = !locked;
	container.innerHTML = locked ? renderChapterMeteredTail(gate === 'login' ? 'login' : 'paywall', requiredPlan, requiredCapability) : '';
}

function paintChapterKeywordCloud(root: Root, response: ChapterApiResponse, summary: string): void {
	const region = byId(root, 'chapter-keywords-region');
	const container = byId(root, 'chapter-keyword-cloud');
	const mobileBlock = byId(root, 'chapter-keywords-mobile-block') as HTMLDetailsElement | null;
	const mobileCloud = byId(root, 'chapter-keyword-cloud-mobile');
	if (!region || !container) return;
	const keywords = response.chapter?.keywords ?? [];
	if (!keywords.length) {
		disposeKeywordCloud(container);
		container.replaceChildren();
		container.classList.remove('kw-cloud-3d', 'kw-cloud--skeleton', 'topic-keyword-cloud--static');
		region.hidden = true;
		if (mobileBlock) mobileBlock.hidden = true;
		return;
	}
	region.hidden = false;
	renderKeywordCloud(container, keywords);
	if (mobileBlock && mobileCloud) {
		mobileBlock.hidden = false;
		mobileBlock.open = false;
		mobileCloud.replaceChildren(...keywords.map((item) => {
			const chip = document.createElement('span');
			chip.className = 'topic-keyword-static';
			chip.style.setProperty('--kw-weight', String(Math.max(0, Math.min(1, item.coefficient / 10))));
			chip.textContent = item.keyword;
			return chip;
		}));
	}
}

function paintTakeaways(root: Root, response: ChapterApiResponse): boolean {
	const container = byId(root, 'article-takeaways');
	if (!container) return false;
	const takeaways = response.chapter?.keyTakeaways;
	if (!takeaways?.length) {
		container.hidden = true;
		(container as HTMLDetailsElement).open = false;
		container.innerHTML = '<summary>Learning outcomes</summary><ul></ul>';
		return false;
	}
	container.hidden = false;
	(container as HTMLDetailsElement).open = false;
	container.innerHTML = `<summary>Learning outcomes</summary><ul>${takeaways
		.map((takeaway) => `<li>${escapeHtml(takeaway)}</li>`)
		.join('')}</ul>`;
	return true;
}

function paintFaqs(root: Root, response: ChapterApiResponse): void {
	const container = byId(root, 'article-faq');
	if (!container) return;
	const faqs = response.chapter?.faqs;
	if (!faqs?.length) {
		container.hidden = true;
		container.innerHTML = '';
		return;
	}
	container.hidden = false;
	container.innerHTML = `<section class="faq"><h2>FAQ</h2>${faqs
		.map((faq) => `<details><summary>${escapeHtml(faq.question)}</summary><p>${escapeHtml(faq.answer)}</p></details>`)
		.join('')}</section>`;
}

function paintMarkdown(body: HTMLElement | null, rendered: HTMLElement | null, locked: boolean, markdown: string): void {
	const completeState = locked ? 'locked' : 'ready';
	if (!rendered) {
		body?.setAttribute('data-state', completeState);
		return;
	}
	const version = (renderVersions.get(rendered) ?? 0) + 1;
	renderVersions.set(rendered, version);
	rendered.hidden = false;
	if (!markdown) {
		rendered.innerHTML = locked
			? '<p>This chapter is part of the premium course. Upgrade to continue reading.</p>'
			: '<p class="loading-placeholder">This chapter has no body yet.</p>';
		body?.setAttribute('data-state', completeState);
		return;
	}
	body?.setAttribute('data-state', 'rendering');
	rendered.innerHTML = '<p class="loading-placeholder">Rendering chapter…</p>';
	void renderMarkdownToSafeHtml(markdown)
		.then((html) => {
			if (renderVersions.get(rendered) !== version) return;
			rendered.innerHTML = html;
			// Markdown is the readable core. Mermaid is a lazy enhancement and
			// must never keep the chapter in rendering state or replace the whole
			// body when its optional chunk/rendering is unavailable.
			body?.setAttribute('data-state', completeState);
			void renderMermaidBlocks(rendered);
		})
		.catch((error) => {
			if (renderVersions.get(rendered) !== version) return;
			rendered.innerHTML = `<pre style="white-space:pre-wrap;color:var(--error);">Render failed: ${escapeHtml(
				error instanceof Error ? error.message : String(error),
			)}\n\n${escapeHtml(markdown)}</pre>`;
			body?.setAttribute('data-state', completeState);
		});
}

function isChapterLocked(response: ChapterApiResponse): boolean {
	return response.access?.text.mode === 'partial';
}
